import debounce from 'lodash.debounce';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Search, Trash2 } from 'react-feather';
import ConfirmModal from '../components/confirm-modal';
import DateRangeSelector from '../components/date-range-selector';
import RemittanceModal from '../components/remittance-modal';
import Table from '../components/table';
import { DateRange } from '../enums/date-range';
import { Order } from '../enums/order';
import { DateRangeData } from '../interfaces/date-range-data';
import { PaginatedDto } from '../interfaces/paginated.dto';
import { Remittance } from '../interfaces/remittance';
import { RemittancesQuery } from '../interfaces/remittances.query';
import { api } from '../services/api';
import { Constants } from '../services/constants';
import { formatNumber, getDatesGivenDateRange } from '../services/helpers';
import myToastr from '../services/toastr';

const dateRange: DateRange = DateRange.ThisYear;

const RemittancesView = () => {
  const tableRef: any = useRef<any>(null);
  const [selectedRemittance, setSelectedRemittance] = useState<Remittance | null>(null);
  const [showConfirmModalRemittance, setShowConfirmModalRemittance] = useState<boolean>(false);
  const [showRemittanceModal, setShowRemittanceModal] = useState(false);
  const [dateRangeData, setDateRangeData] = useState<DateRangeData>({
    dateRange,
    dates: getDatesGivenDateRange(dateRange),
  });
  const [params, setParams] = useState<RemittancesQuery>({
    endDate: moment(dateRangeData.dates[1]).format('YYYY-MM-DD HH:mm'),
    limit: Constants.LIMIT_RESULTS,
    order: Order.Desc,
    orderField: 'remittance.createdAt',
    page: 1,
    startDate: moment(dateRangeData.dates[0]).format('YYYY-MM-DD HH:mm'),
    query: undefined,
  });
  const [query, setQuery] = useState('');
  const [paginatedData, setPaginatedData] = useState<PaginatedDto<Remittance> | null>(null);

  const debounced = useMemo(
    () =>
      debounce((nextValue: string) => {
        setParams({
          ...params,
          query: nextValue.length > 0 ? nextValue : undefined,
          page: 1,
        });
      }, 1000),
    [params],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Número',
        disableFilters: true,
        accessor: 'internalId',
        filter: 'fuzzyText',
        Cell: ({ value }: any) => {
          return <span className="fw-bold">{value}</span>;
        },
      },
      {
        Header: 'Fecha',
        disableFilters: true,
        accessor: (remittance: Remittance) => moment(remittance.createdAt).format('DD/MM/YYYY'),
      },
      {
        Header: 'Usuario',
        disableFilters: true,
        accessor: (remittance: Remittance) => (remittance.user ? `${remittance.user.name} ${remittance.user.surnames}`.trim() : ''),
      },
      {
        Header: 'Vencimientos',
        disableFilters: true,
        accessor: (remittance: Remittance) => remittance.expirationPayments.length,
      },
      {
        Header: 'Total',
        disableFilters: true,
        accessor: (remittance: Remittance) => {
          const total = remittance.expirationPayments.reduce((sum, expirationPayment) => sum + expirationPayment.amount, 0);
          return `${formatNumber(total)} €`;
        },
      },
      {
        Header: 'Acciones',
        disableFilters: true,
        Cell: ({ row }: any) => {
          const remittance = row.original as Remittance;
          return (
            <div className="d-flex flex-row align-items-center gap-2">
              <div title="Ver remesa">
                <Search
                  color="#222E3D"
                  onClick={() => {
                    setSelectedRemittance(remittance);
                    setShowRemittanceModal(true);
                  }}
                  size={16}
                  type="button"
                />
              </div>
              <div title="Descargar fichero">
                <Download onClick={() => downloadXML(remittance)} size={14} type="button" />
              </div>
              <div title="Eliminar remesa">
                <Trash2
                  onClick={() => {
                    setSelectedRemittance(remittance);
                    setShowConfirmModalRemittance(true);
                  }}
                  size={14}
                  type="button"
                />
              </div>
            </div>
          );
        },
      },
    ],
    [],
  );

  const getExpirations = useCallback(async () => {
    try {
      const data = await api.getRemittances(params);
      setPaginatedData(data);
    } catch (e: any) {
      myToastr.error(e.response.data.message);
    }
  }, [params]);

  const downloadXML = async (remittance: Remittance) => {
    try {
      const xmlContent = await api.getRemmitanceXML(remittance.id);
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${remittance.internalId}.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      myToastr.error(e.response.data.message);
    }
  };

  const onDeleteRemittance = async (result: boolean) => {
    if (result) {
      try {
        await api.deleteRemittance(selectedRemittance!.id);
        await getExpirations();
        myToastr.success('Remesa eliminada correctamente');
      } catch (e: any) {
        myToastr.error(e.response.data.message);
      }
    }
    setShowConfirmModalRemittance(false);
    setSelectedRemittance(null);
  };

  useEffect(() => {
    getExpirations();
  }, [params]);

  useEffect(() => {
    return () => {
      debounced.cancel();
    };
  }, [debounced]);

  return (
    <>
      <div className="expirations p-4">
        <div className="d-flex flex-row align-items-center justify-content-between mb-4">
          <h1>Remesas</h1>
          <div className="d-flex flex-row align-items-center">
            <input
              className="me-2"
              onChange={(e) => {
                const nextValue = e.target.value;
                setQuery(nextValue);
                debounced(nextValue);
              }}
              placeholder="Buscar..."
              style={{ padding: '4px 10px', fontSize: 14, width: 250, border: '1px solid black', borderRadius: 8 }}
              value={query}
              type="text"
            />
            <div style={{ width: 200 }}>
              <DateRangeSelector
                showTimePicker={false}
                dateRangeData={dateRangeData}
                availableDateRanges={[DateRange.Today, DateRange.Yesterday, DateRange.ThisWeek, DateRange.ThisMonth, DateRange.ThisYear, DateRange.LastYear]}
                onChange={(drd: DateRangeData) => {
                  setDateRangeData(drd);
                  setParams({
                    ...params,
                    startDate: moment(drd.dates[0]).format('YYYY-MM-DD HH:mm'),
                    endDate: moment(drd.dates[1]).format('YYYY-MM-DD HH:mm'),
                    page: 1,
                  });
                }}
              />
            </div>
          </div>
        </div>
        <Table
          columns={columns}
          data={paginatedData?.results || []}
          initialState={{
            sortBy: [{ id: 'expirationDate', desc: true }],
            pageSize: Constants.LIMIT_RESULTS,
          }}
          noDataMessage="No hay remesas"
          ref={tableRef}
        />
        {selectedRemittance && (
          <ConfirmModal
            acceptButtonClass="accept-button"
            show={showConfirmModalRemittance}
            title={`Eliminar Remesa ${selectedRemittance.internalId}`}
            content="¿Estás seguro que quieres eliminar la remesa?"
            closeModal={onDeleteRemittance}
          />
        )}
      </div>
      <RemittanceModal
        closeModal={() => {
          setSelectedRemittance(null);
          setShowRemittanceModal(false);
        }}
        onExpirationPaymentUpdate={() => getExpirations()}
        remittanceId={selectedRemittance?.id}
        show={showRemittanceModal}
      />
    </>
  );
};

export default RemittancesView;
